// Pure, presentation-agnostic adapters that map a HITL interaction's requested
// `fields` (the agent's `{{name}}` placeholders) to a credential form shape and
// back to the flat KV the transient broker submit expects.
//
// SECURITY (G4 presentational-only / G7 untrusted input): `interaction.fields`
// is agent-authored, so it is treated as fully untrusted here. These helpers
// NEVER eval, interpolate, or render field names as markup — they only
// classify, clamp, whitelist, and copy string values. `pickFields` emits ONLY
// the requested names, so a stray form-state key (e.g. the cosmetic `label`)
// can never leak into the broker payload.
//
// This module is pure (no React, no I/O) so it is exhaustively unit-testable.

/** Hard cap on how many fields a single agent request can drive (G7 clamp). */
export const MAX_INTERACTION_FIELDS = 12;

export type FieldKind =
  | 'username'
  | 'password'
  | 'totpSecret'
  | 'totpCode'
  | 'token'
  | 'generic';

/** Which form body a requested field-shape renders. */
export type FormShape =
  | 'basic' // username + password
  | 'basicTotp' // username + password + a TOTP field
  | 'singleToken' // one masked token / lone field
  | 'totpCode' // one live 6-digit code field
  | 'dynamic'; // arbitrary / mixed names → editable name-value card

// A live TOTP/OTP CODE (a 6-digit value derived per attempt) — checked BEFORE
// the seed pattern so `otp` / `mfa` never collapse into the seed classification.
const TOTP_CODE_RE = /^(totp_code|otp|otp_code|mfa|mfa_code|2fa)$/i;
// A TOTP SEED (base32) — used by TOTPSecretInput; the recorder derives codes.
const TOTP_SECRET_RE = /^(totp_secret|totp_seed|totp)$/i;
const TOKEN_RE =
  /^(token|api_key|apikey|bearer|bearer_token|secret|access_token|api_token)$/i;

/**
 * Classifies a single requested field name into a known credential KIND, or
 * `generic` when unrecognized. Whitelist-only: an unknown name is never coerced
 * into a privileged kind.
 */
export function classifyField(name: string): FieldKind {
  const n = name.trim().toLowerCase();
  if (n === 'username') return 'username';
  if (n === 'password') return 'password';
  if (TOTP_CODE_RE.test(n)) return 'totpCode';
  if (TOTP_SECRET_RE.test(n)) return 'totpSecret';
  if (TOKEN_RE.test(n)) return 'token';
  return 'generic';
}

/**
 * Hardens the agent-supplied `fields` array (G7): drops non-strings and
 * blank entries, dedupes case-insensitively (first occurrence wins), and clamps
 * to {@link MAX_INTERACTION_FIELDS}. Returns a fresh array (never mutates input).
 */
export function normalizeRequestedFields(fields?: string[]): string[] {
  if (!Array.isArray(fields)) return [];
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of fields) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(trimmed);
  }
  return unique.slice(0, MAX_INTERACTION_FIELDS);
}

/**
 * Selects the form body for a requested field-shape. `fields` is the normalized,
 * effective field list (falls back to a single generic `input` when the agent
 * requested none, mirroring the legacy widget).
 */
export function selectFormShape(fields?: string[]): {
  shape: FormShape;
  fields: string[];
} {
  const norm = normalizeRequestedFields(fields);
  const effective = norm.length > 0 ? norm : ['input'];
  const kinds = effective.map(classifyField);
  const hasUser = kinds.includes('username');
  const hasPass = kinds.includes('password');

  if (hasUser && hasPass) {
    // Only route to the basic form when EVERY effective field is one the basic
    // body can actually render (username/password/totp). A leftover field
    // (e.g. `domain`, `api_key`) has no input there and pickFields would
    // silently drop it from the broker payload — so fall through to `dynamic`,
    // which pre-fills and renders every requested field name (N-1).
    const allBasicRenderable = kinds.every(
      k =>
        k === 'username' ||
        k === 'password' ||
        k === 'totpSecret' ||
        k === 'totpCode'
    );
    if (allBasicRenderable) {
      const hasTotp =
        kinds.includes('totpSecret') || kinds.includes('totpCode');
      return { shape: hasTotp ? 'basicTotp' : 'basic', fields: effective };
    }
  }

  if (effective.length === 1) {
    return {
      shape: kinds[0] === 'totpCode' ? 'totpCode' : 'singleToken',
      fields: effective,
    };
  }

  return { shape: 'dynamic', fields: effective };
}

/**
 * Pure whitelist adapter (G4/G7): builds the flat `{ name: value }` map the
 * ephemeral broker submit expects from the current form state. Emits ONLY the
 * requested names, and only those with a non-empty trimmed value — so cosmetic
 * / unrequested state keys can never reach the broker. Never mutates inputs.
 */
export function pickFields(
  values: Record<string, string>,
  requested: string[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const name of requested) {
    const value = values[name];
    if (typeof value === 'string' && value.trim() !== '') {
      out[name] = value;
    }
  }
  return out;
}
